// include directives/components commonly used in features modules in this shared modules
// and import me into the feature module
// importing them individually results in: Type xxx is part of the declarations of 2 modules: ... Please consider moving to a higher module...
// https://github.com/angular/angular/issues/10646

import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { CoreModule, FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { RecaptchaFormsModule, RecaptchaModule } from 'ng-recaptcha-2';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { NgxSpinnerModule } from 'ngx-spinner';
import { CoreModule as CoreModuleCustom } from '../core/core.module';
import { MaterialModule } from '../core/material.module';
import { DndDirective } from './directives/dnd.directive';
import { I18nModule } from '../i18n/i18n.module';
import { CustomPdfViewerComponent } from './components/custom-pdf-viewer/custom-pdf-viewer.component';
import { FileUploadComponent } from './components/fileUpload/fileUpload.component';
import { ChoiceFormQuestionComponent } from './components/forms/choice-form-question-component/choice-form-question.component';
import { FileFormQuestionComponent } from './components/forms/file-form-question-component/file-form-question.component';
import { TextBoxFormQuestionComponent } from './components/forms/textBoxFormQuestionComponent/textBoxFormQuestion.component';
import { IconComponent } from './components/icon/icon.component';
import { SnackBarComponent } from './components/snack-bar/snack-bar.component';
import { AutofocusDirective } from './directives/auto-focus.directive';
import { DecimalNumbersDirective } from './directives/decimal-numbers.directive';
import { NumbersOnlyDirective } from './directives/numbers-only.directive';
import { VisibleWithPermissionsDirective } from './directives/visible-with-permissions.directive';
import { VisibleWithPermissionsOrMyProfileDirective } from './directives/visible-with-permissions-or-my-profile.directive';
import { BankAccountInputMaskingDirective } from './directives/bank-account-input-masking.directive';
import { TimespanToStringPipe } from './pipes/timespan-to-string.pipe';
import { TicksToStringPipe } from './pipes/ticks-to-string.pipe';
import { ConfirmDialogComponent } from './messages/confirm-dialog/confirm-dialog.component';
import { AlertDialogComponent } from './messages/alert-dialog/alert-dialog.component';
import { UppercaseInputDirective } from './directives/uppercase-input.directive';
import { DayOfWeekPipe } from './pipes/day-of-week.pipe';
import { NavbarUserMenuComponent } from './components/navbar-components/navbar-user-menu/navbar-user-menu.component';
import { NavbarNotificationsComponent } from './components/navbar-components/navbar-notifications/navbar-notifications.component';
import { NavbarInfoComponent } from './components/navbar-components/navbar-info/navbar-info.component';
import { NavbarSearchComponent } from './components/navbar-components/navbar-search/navbar-search.component';
import { StatusInfoComponent } from './components/status-info/status-info.component';
import { UserAvatarCircleComponent } from './components/user-avatar-circle/user-avatar-circle.component';
import { BankAccountFormatPipe } from './pipes/bank-account-format.pipe';
import { SignaturePadComponent } from './components/signature-pad/signature-pad.component';
import { NavbarChooseLanguageComponent } from './components/navbar-components/navbar-choose-language/navbar-choose-language.component';
import { AlphanumericDirective } from './directives/alphanumeric.directive';
import { CdkStepper } from '@angular/cdk/stepper';
import { AlertComponent } from './components/alert/alert.component';
import { TermsAndConditionsComponent } from './components/terms-and-conditions/terms-and-conditions.component';
import { FilterPurelyPipe } from './pipes/filter-purely.pipe';
import { TreeviewComponent } from './components/treeview/treeview.component';
import { FilterWageTypePipe } from './pipes/filter-wage-type.pipe';
import { MaxRangeDirective } from './directives/max-range-directive';
import { FilterPipe } from './pipes/filter.pipe';
import { FindPurelyPipe } from './pipes/find-purely.pipe';
import { ExpandableTableComponent } from './components/expandable-table/expandable-table.component';
import { TableStatusInfoComponent } from './components/table-status-info/table-status-info.component';
import { RefreshComponent } from './components/refresh-component/refresh.component';
import { NumericOnlyWtihTwoDecimalsDirective } from './directives/numbers-only-with-two-decimal.directive';
import { ExtendedAlertDialogComponent } from './messages/extended-alert-dialog/extended-alert-dialog.component';
import { NgxSignaturePadModule } from '../ngx-signature-pad';
import { PostCodeMaskDirective } from './directives/post-code-mask.directive';
import { VarDirective } from './directives/var.directive';
import { FindFirstOrNextIdPipe } from './pipes/find-first-or-next-id.pipe';
import { EmployeeDirective } from "./directives/employee.directive";
import { SubscriptionPackageModule, SubscriptionPackageService } from '../subscription-package';
import { SubscriptionPackageService as SubscriptionPackageApiService } from '../data/subscription-package.service';
import { CustomPacmanSpinnerComponent } from './components/custom-pacman-spinner/custom-pacman-spinner.component';
import { IfHasPermissionsOnWorkerProfileDirective } from './directives/if-has-permissions-on-worker-profile.directive';
import { IfHasPermissionsDirective } from './directives/if-has-permissions.directive';
import { TableHeaderSortComponent } from "./components/table-header-sort/table-header-sort.component";
import { MultipleChoiceQuestionComponent } from './components/forms/multiple-choice-question/multiple-choice-question.component';
import { FormQuestionContainerComponent } from './components/forms/form-question-container/form-question-container.component';
import { DateYearFormQuestionInputComponent } from './components/forms/date-year-form-question-input/date-year-form-question-input.component';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { MarkdownPipe } from './pipes/markdown.pipe';
import { CustomImageViewerComponent } from './components/custom-image-viewer/custom-image-viewer.component';
import { NgxImageZoomModule } from 'ngx-image-zoom';
import { TreeViewerComponent } from './components/tree-viewer/tree-viewer.component';
import { FilterPresetNameFormDialogComponent } from './components/filters/filter-preset-name-form-dialog/filter-preset-name-form-dialog.component';
import { PrivacyPolicyComponent } from './components/privacy-policy/privacy-policy.component';
import { ItemCounterComponent } from './components/item-counter/item-counter.component';
import { WarningDialogComponent } from './messages/warning-dialog/warning-dialog.component';
import { AnotherUserLoginComponent } from './messages/another-user-login/another-user-login.component';
import { HasPermissionDirective } from './directives/has-permission.directive';
import { AdressFormComponent } from './components/adress-form/adress-form.component';
import { AbroadAdressFormComponent } from './components/abroad-adress-form/abroad-adress-form.component';
import { PolishAdressFormComponent } from './components/polish-adress-form/polish-adress-form.component';
import { AdressesFormComponent } from './components/adresses-form/adresses-form.component';
import { TextBoxOrChoiceFormQuestionComponent } from './components/forms/textBoxOrChoiceFormQuestionComponent/textBoxOrChoiceFormQuestion.component';
import { FilterWageTypeMaxCountPipe } from './pipes/filter-wage-type-max-count.pipe';
import { HoursAndMinutesPipe } from './pipes/hours-and-minutes.pipe';
import { NgxMaskPipe, NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { TooltipListPipe } from './pipes/tooltip-list.pipe';
import { WeekPipe } from './pipes/week.pipe';
import { WeekYearPipe } from './pipes/week-year.pipe';
import { WeekRangePipe } from './pipes/week-range.pipe';
import { DateAgoPipe } from './pipes/date-ago.pipe';
import { NotificationModalComponent } from './components/notification-modal/notification-modal.component';
import { CopyDirective } from './directives/copy/copy.directive';
import { LegalizationStatusInfoComponent } from './components/legalization-status-info/legalization-status-info.component';
import { CopyComponent } from './components/copy/copy.component';
import { CustomMultipleFileViewerComponent } from './components/custom-multiple-file-viewer/custom-multiple-file-viewer.component';
import { ApprovalProgressBadgeComponent } from './components/approval-progress-badge/approval-progress-badge.component';
import { AnnexBadgeComponent } from './components/annex-badge/annex-badge.component';
import { AnnexImportedBadgeComponent } from './components/annex-imported-badge/annex-imported-badge.component';
import { WorkerAgreementDetailsSharedComponent } from '../worker-agreements/shared/worker-agreement-details-shared/worker-agreement-details-shared.component';
import { EmploymentAgreementDetailsSharedComponent } from '../worker-agreements/shared/employment-agreement-details-shared/employment-agreement-details-shared.component';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { StripHtmlPipe } from './pipes/strip-html.pipe';
import { PropertyWithHistoryComponent } from './components/property-with-history/property-with-history.component';
import { WagesWithHistoryComponent } from './components/wages-with-history/wages-with-history.component';
import { TextCaseDirective } from './directives/text-case/text-case.directive';
import { ResponsibilityMultiSelectComponent } from './components/responsibility-multi-select/responsibility-multi-select.component';
import { TrimOnBlurDirective } from './directives/trim-on-blur.directive';
import { YearPickerComponent } from './components/year-picker/year-picker.component';

//https://stackoverflow.com/questions/41433766/directive-doesnt-work-in-a-sub-module
//https://stackoverflow.com/questions/45032043/uncaught-error-unexpected-module-formsmodule-declared-by-the-module-appmodul/45032201

@NgModule({
  imports: [
    MarkdownPipe,
    CommonModule,
    CoreModule,
    CoreModuleCustom,
    NgxSpinnerModule,
    RouterModule,
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    I18nModule,
    PdfViewerModule,
    NgxImageZoomModule,
    FlexLayoutModule,
    NgxSignaturePadModule,
    SubscriptionPackageModule,
    RecaptchaModule,
    RecaptchaFormsModule,
    BrowserAnimationsModule,
    NgxMatSelectSearchModule,
    NgxMaskDirective,
    NgxMaskPipe,
    DateAgoPipe,
    WeekPipe,
    WeekYearPipe,
    WeekRangePipe,
    CopyDirective,
    StripHtmlPipe,
    TextCaseDirective,
    AnnexImportedBadgeComponent
  ],
  declarations: [
    AutofocusDirective,
    DndDirective,
    NumbersOnlyDirective,
    NumericOnlyWtihTwoDecimalsDirective,
    DecimalNumbersDirective,
    BankAccountInputMaskingDirective,
    AlphanumericDirective,
    IconComponent,
    FileUploadComponent,
    ChoiceFormQuestionComponent,
    TextBoxFormQuestionComponent,
    TextBoxOrChoiceFormQuestionComponent,
    FileFormQuestionComponent,
    CustomPdfViewerComponent,
    CustomImageViewerComponent,
    CustomMultipleFileViewerComponent,
    SnackBarComponent,
    VisibleWithPermissionsDirective,
    VisibleWithPermissionsOrMyProfileDirective,
    TimespanToStringPipe,
    TicksToStringPipe,
    DayOfWeekPipe,
    BankAccountFormatPipe,
    ConfirmDialogComponent,
    AlertDialogComponent,
    ExtendedAlertDialogComponent,
    UppercaseInputDirective,
    NavbarUserMenuComponent,
    NavbarNotificationsComponent,
    NavbarInfoComponent,
    NavbarSearchComponent,
    StatusInfoComponent,
    UserAvatarCircleComponent,
    SignaturePadComponent,
    NavbarChooseLanguageComponent,
    AlertComponent,
    TermsAndConditionsComponent,
    FindPurelyPipe,
    FilterPurelyPipe,
    FilterPipe,
    FilterWageTypePipe,
    FilterWageTypeMaxCountPipe,
    TreeviewComponent,
    MaxRangeDirective,
    ExpandableTableComponent,
    TableStatusInfoComponent,
    RefreshComponent,
    PostCodeMaskDirective,
    VarDirective,
    FindFirstOrNextIdPipe,
    EmployeeDirective,
    CustomPacmanSpinnerComponent,
    IfHasPermissionsOnWorkerProfileDirective,
    IfHasPermissionsDirective,
    MultipleChoiceQuestionComponent,
    TableHeaderSortComponent,
    FormQuestionContainerComponent,
    DateYearFormQuestionInputComponent,
    SafeHtmlPipe,
    TreeViewerComponent,
    FilterPresetNameFormDialogComponent,
    PrivacyPolicyComponent,
    ItemCounterComponent,
    WarningDialogComponent,
    AnotherUserLoginComponent,
    HasPermissionDirective,
    AdressFormComponent,
    PolishAdressFormComponent,
    AbroadAdressFormComponent,
    AdressesFormComponent,
    HoursAndMinutesPipe,
    TooltipListPipe,
    NotificationModalComponent,
    LegalizationStatusInfoComponent,
    CopyComponent,
    ApprovalProgressBadgeComponent,
    AnnexBadgeComponent,
    WorkerAgreementDetailsSharedComponent,
    EmploymentAgreementDetailsSharedComponent,
    PropertyWithHistoryComponent,
    WagesWithHistoryComponent,
    ResponsibilityMultiSelectComponent,
    TrimOnBlurDirective,
    YearPickerComponent
  ],
  exports: [
    FlexLayoutModule,
    FormsModule,
    I18nModule,
    MaterialModule,
    NgxSpinnerModule,
    ReactiveFormsModule,
    RecaptchaModule,
    RecaptchaFormsModule,
    RouterModule,
    AutofocusDirective,
    DecimalNumbersDirective,
    AlphanumericDirective,
    NumbersOnlyDirective,
    NumericOnlyWtihTwoDecimalsDirective,
    ChoiceFormQuestionComponent,
    CustomPdfViewerComponent,
    FileFormQuestionComponent,
    FileUploadComponent,
    IconComponent,
    TextBoxFormQuestionComponent,
    TextBoxOrChoiceFormQuestionComponent,
    VisibleWithPermissionsDirective,
    VisibleWithPermissionsOrMyProfileDirective,
    TimespanToStringPipe,
    TicksToStringPipe,
    DayOfWeekPipe,
    BankAccountFormatPipe,
    ConfirmDialogComponent,
    AlertDialogComponent,
    UppercaseInputDirective,
    BankAccountInputMaskingDirective,
    NavbarUserMenuComponent,
    NavbarNotificationsComponent,
    NavbarSearchComponent,
    NavbarInfoComponent,
    StatusInfoComponent,
    UserAvatarCircleComponent,
    SignaturePadComponent,
    NavbarChooseLanguageComponent,
    AlertComponent,
    TermsAndConditionsComponent,
    FindPurelyPipe,
    FilterPurelyPipe,
    FilterPipe,
    FilterWageTypePipe,
    FilterWageTypeMaxCountPipe,
    MaxRangeDirective,
    ExpandableTableComponent,
    TableStatusInfoComponent,
    PostCodeMaskDirective,
    VarDirective,
    FindFirstOrNextIdPipe,
    EmployeeDirective,
    CustomPacmanSpinnerComponent,
    SubscriptionPackageModule,
    IfHasPermissionsOnWorkerProfileDirective,
    IfHasPermissionsDirective,
    TableHeaderSortComponent,
    SafeHtmlPipe,
    MarkdownPipe,
    ItemCounterComponent,
    AnotherUserLoginComponent,
    HasPermissionDirective,
    AdressesFormComponent,
    CommonModule,
    CoreModule,
    CoreModuleCustom,
    BrowserAnimationsModule,
    HoursAndMinutesPipe,
    NgxMatSelectSearchModule,
    NgxMaskDirective,
    NgxMaskPipe,
    TooltipListPipe,
    DateAgoPipe,
    NotificationModalComponent,
    CopyComponent,
    WeekPipe,
    WeekYearPipe,
    WeekRangePipe,
    CopyDirective,
    LegalizationStatusInfoComponent,
    ApprovalProgressBadgeComponent,
    AnnexBadgeComponent,
    AnnexImportedBadgeComponent,
    WorkerAgreementDetailsSharedComponent,
    EmploymentAgreementDetailsSharedComponent,
    StripHtmlPipe,
    PropertyWithHistoryComponent,
    WagesWithHistoryComponent,
    TextCaseDirective,
    ResponsibilityMultiSelectComponent,
    TrimOnBlurDirective,
    YearPickerComponent
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    CdkStepper,
    {
      provide: SubscriptionPackageService,
      useClass: SubscriptionPackageApiService
    },
    provideNgxMask()
  ],
})
export class SharedModule { }
